import { DeleteOutlined, EditOutlined, LeftOutlined, PlusOutlined } from "@ant-design/icons";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button, Input, Modal, message, Popconfirm, Select } from "antd";
import { useEffect, useState } from "react";
import { useLocation, useNavigate, useOutletContext, useSearchParams } from "react-router-dom";
import { useAuthStore } from "@/modules/auth/store/auth.store";
import { organizationsApi } from "@/modules/organizations/api/organizations.api";
import type { Organization } from "@/modules/organizations/types/organization.types";
import { UzPhoneInput } from "@/shared/components/UzPhoneInput";
import type { AppRole } from "@/shared/config/navigation";
import { canManageUsers } from "@/shared/config/permissions";
import { parseApiError } from "@/shared/utils/error";
import { normalizeUzPhoneInput } from "@/shared/utils/uz-phone";
import { UsersNavigation } from "../components/UsersNavigation";
import {
	type AdminRole,
	type AdminUser,
	type AdminUserUpdatePayload,
	getAdminRoles,
	getAdminUserById,
	getAdminUsers,
	isManagedUserVisible,
	normalizeUserPhone,
	submitUserCreate,
	submitUserDelete,
	submitUserUpdate,
} from "../services/users.backend";

const avatarColors = ["blue", "green", "sky", "violet", "amber"] as const;

function getInitials(fullName: string) {
	return fullName
		.split(" ")
		.map((part) => part[0] ?? "")
		.join("")
		.slice(0, 2)
		.toUpperCase();
}

function getAvatarColor(id: string) {
	const index =
		id.split("").reduce((sum, char) => sum + char.charCodeAt(0), 0) % avatarColors.length;
	return avatarColors[index];
}

function formatLastLogin(value: null | string) {
	if (!value) {
		return "-";
	}

	return new Intl.DateTimeFormat("uz-UZ", {
		day: "2-digit",
		hour: "2-digit",
		minute: "2-digit",
		month: "2-digit",
		year: "numeric",
	}).format(new Date(value));
}

const ROLE_NAME_MAP: Record<string, string> = {
	SUPER_ADMIN: "Super Administrator",
	ORG_ADMIN: "Tashkilot Administratori",
	SALES_MANAGER: "Sotuv Menejeri",
	super_admin: "Super Administrator",
	org_admin: "Tashkilot Administratori",
	sales_manager: "Sotuv Menejeri",
};

function getRoleLabel(role: null | string, roles: AdminRole[]) {
	if (!role) {
		return "-";
	}

	const mapping: Record<string, string> = {
		super: "super_admin",
		admin: "org_admin",
		manager: "sales_manager",
	};
	const target = mapping[role] || role;

	return (
		roles.find((item) => item.id === target || item.name === target || item.id === role)?.name ??
		role
	);
}

function getRoleId(role: null | string, roles: AdminRole[]) {
	if (!role) {
		return undefined;
	}

	const mapping: Record<string, string> = {
		super: "super_admin",
		admin: "org_admin",
		manager: "sales_manager",
	};
	const target = mapping[role] || role;

	return (
		roles.find((item) => item.id === target || item.name === target || item.id === role)?.id ?? role
	);
}

function getBackendRoleId(role?: string) {
	switch (role) {
		case "admin":
		case "ORG_ADMIN":
		case "org_admin":
			return "org_admin";
		case "manager":
		case "SALES_MANAGER":
		case "sales_manager":
			return "sales_manager";
		default:
			return role;
	}
}

function getCreatableRoles(currentRole: AppRole, roles: AdminRole[]) {
	const allowedBackendRoles = currentRole === "super" ? ["org_admin"] : ["sales_manager"];
	return roles.filter((role) =>
		allowedBackendRoles.includes(getBackendRoleId(role.id) || getBackendRoleId(role.name) || "")
	);
}

type AddModalProps = {
	canManage: boolean;
	currentRole: AppRole;
	isSubmitting: boolean;
	open: boolean;
	roles: AdminRole[];
	rolesLoading: boolean;
	rolesError: boolean;
	organizations: Organization[];
	organizationsLoading: boolean;
	organizationsError: boolean;
	onClose: () => void;
	onSubmit: (payload: {
		email: string;
		fullName: string;
		orgId?: string;
		password: string;
		roleId?: string;
		username: string;
		phone: string;
	}) => void;
};

function AddUserModal({
	canManage,
	currentRole,
	isSubmitting,
	open,
	roles,
	rolesLoading,
	rolesError,
	organizations,
	organizationsLoading,
	organizationsError,
	onClose,
	onSubmit,
}: AddModalProps) {
	const [firstName, setFirstName] = useState("");
	const [lastName, setLastName] = useState("");
	const [username, setUsername] = useState("");
	const [email, setEmail] = useState("");
	const [password, setPassword] = useState("");
	const [phone, setPhone] = useState("");
	const [roleId, setRoleId] = useState<string>();
	const [orgId, setOrgId] = useState<string>();
	const creatableRoles = getCreatableRoles(currentRole, roles);
	const needsOrganization = currentRole === "super";

	useEffect(() => {
		if (!open) {
			return;
		}
		if (!roleId && creatableRoles.length === 1) {
			setRoleId(creatableRoles[0].id);
		}
		if (!orgId && needsOrganization && organizations.length === 1) {
			setOrgId(organizations[0].id);
		}
	}, [creatableRoles, needsOrganization, open, organizations, orgId, roleId]);

	const resetForm = () => {
		setFirstName("");
		setLastName("");
		setUsername("");
		setEmail("");
		setPassword("");
		setPhone("");
		setRoleId(undefined);
		setOrgId(undefined);
	};

	const handleClose = () => {
		if (isSubmitting) {
			return;
		}

		onClose();
		resetForm();
	};

	const handleSubmit = async () => {
		if (!canManage) {
			message.warning("Bu rol foydalanuvchilarni boshqara olmaydi.");
			return;
		}

		if (
			!(
				firstName.trim() &&
				lastName.trim() &&
				username.trim() &&
				email.trim() &&
				phone.trim() &&
				password &&
				roleId
			)
		) {
			message.warning("Barcha majburiy maydonlarni to'ldiring.");
			return;
		}

		if (needsOrganization && !orgId) {
			message.warning("Tashkilotni tanlang.");
			return;
		}

		const normalizedPhone = normalizeUserPhone(phone);
		if (!normalizedPhone) {
			message.warning("Telefon raqamni +998901234567 formatida kiriting.");
			return;
		}

		onSubmit({
			email: email.trim(),
			fullName: `${firstName.trim()} ${lastName.trim()}`,
			orgId: needsOrganization ? orgId : undefined,
			password,
			roleId,
			username: username.trim(),
			phone: normalizedPhone,
		});
	};

	return (
		<Modal
			open={open}
			onCancel={handleClose}
			footer={null}
			title="Yangi foydalanuvchi"
			width={560}
			className="crm-users-modal"
		>
			<div className="crm-users-modal-body">
				<div className="crm-inline-fields">
					<label className="crm-field" htmlFor="admin-user-first-name">
						<span>Ism</span>
						<Input
							id="admin-user-first-name"
							placeholder="Kamol"
							value={firstName}
							onChange={(e) => setFirstName(e.target.value)}
						/>
					</label>
					<label className="crm-field" htmlFor="admin-user-last-name">
						<span>Familiya</span>
						<Input
							id="admin-user-last-name"
							placeholder="Xolmatov"
							value={lastName}
							onChange={(e) => setLastName(e.target.value)}
						/>
					</label>
				</div>

				<div className="crm-inline-fields">
					<label className="crm-field" htmlFor="admin-user-username">
						<span>Username (login)</span>
						<Input
							id="admin-user-username"
							placeholder="kamol"
							value={username}
							onChange={(e) => setUsername(e.target.value)}
						/>
					</label>

					<label className="crm-field" htmlFor="admin-user-email">
						<span>Email</span>
						<Input
							id="admin-user-email"
							placeholder="kamol@example.com"
							value={email}
							onChange={(e) => setEmail(e.target.value)}
						/>
					</label>
				</div>

				<div className="crm-inline-fields">
					<label className="crm-field" htmlFor="admin-user-phone">
						<span>Telefon raqam</span>
						<UzPhoneInput id="admin-user-phone" value={phone} onChange={setPhone} />
					</label>

					<label className="crm-field" htmlFor="admin-user-password">
						<span>Parol</span>
						<Input.Password
							id="admin-user-password"
							placeholder="********"
							value={password}
							onChange={(e) => setPassword(e.target.value)}
						/>
					</label>
				</div>

				<div className="crm-inline-fields">
					{needsOrganization ? (
						<label className="crm-field" htmlFor="admin-user-organization">
							<span>Tashkilot</span>
							<Select
								id="admin-user-organization"
								placeholder="Tashkilotni tanlang"
								value={orgId}
								onChange={setOrgId}
								style={{ width: "100%" }}
								loading={organizationsLoading}
								notFoundContent={
									organizationsError ? "Tashkilotlarni yuklab bo'lmadi" : "Tashkilotlar topilmadi"
								}
								options={organizations.map((item) => ({ label: item.name, value: item.id }))}
							/>
						</label>
					) : null}
					<label className="crm-field" htmlFor="admin-user-role">
						<span>Rol</span>
						<Select
							id="admin-user-role"
							placeholder="Rolni tanlang"
							value={roleId}
							onChange={setRoleId}
							style={{ width: "100%" }}
							loading={rolesLoading}
							notFoundContent={rolesError ? "Ro'llarni yuklab bo'lmadi" : "Ro'llar topilmadi"}
							options={creatableRoles.map((item) => ({
								label: ROLE_NAME_MAP[item.name] || item.name,
								value: item.id,
							}))}
						/>
					</label>
				</div>

				<div className="crm-users-modal-footer">
					<Button disabled={isSubmitting} onClick={handleClose}>
						Bekor
					</Button>
					<Button
						type="primary"
						className="crm-primary-btn"
						loading={isSubmitting}
						onClick={handleSubmit}
					>
						Qo'shish
					</Button>
				</div>
			</div>
		</Modal>
	);
}

type EditModalProps = {
	canManage: boolean;
	currentRole: AppRole;
	isSubmitting: boolean;
	open: boolean;
	roles: AdminRole[];
	rolesLoading: boolean;
	rolesError: boolean;
	user: AdminUser | null;
	onClose: () => void;
	onSubmit: (
		userId: string,
		payload: {
			email?: string;
			fullName?: string;
			isActive?: boolean;
			password?: string;
			roleId?: string;
			username?: string;
			phone?: string;
		}
	) => void;
};

function EditUserModal({
	canManage,
	currentRole,
	isSubmitting,
	open,
	roles,
	rolesLoading,
	rolesError,
	user,
	onClose,
	onSubmit,
}: EditModalProps) {
	const [firstName, setFirstName] = useState("");
	const [lastName, setLastName] = useState("");
	const [username, setUsername] = useState("");
	const [email, setEmail] = useState("");
	const [password, setPassword] = useState("");
	const [phone, setPhone] = useState("");
	const [roleId, setRoleId] = useState<string>();
	const [isActive, setIsActive] = useState(true);
	const editableRoles = getCreatableRoles(currentRole, roles);

	useEffect(() => {
		if (!(user && open)) {
			return;
		}

		const parts = user.fullName.split(" ");
		setFirstName(parts[0] || "");
		setLastName(parts.slice(1).join(" ") || "");
		setUsername(user.username || "");
		setEmail(user.email || "");
		setPhone(normalizeUzPhoneInput(user.phone || ""));
		setPassword("");
		setRoleId(getRoleId(user.role, roles));
		setIsActive(user.isActive);
	}, [roles, user, open]);

	const handleClose = () => {
		if (isSubmitting) {
			return;
		}

		onClose();
	};

	const handleSubmit = async () => {
		if (!canManage) {
			message.warning("Bu rol foydalanuvchilarni boshqara olmaydi.");
			return;
		}

		if (!user) {
			return;
		}

		const normalizedPhone = normalizeUserPhone(phone);
		if (!normalizedPhone) {
			message.warning("Telefon raqamni +998901234567 formatida kiriting.");
			return;
		}

		const payload: any = {
			fullName: `${firstName.trim()} ${lastName.trim()}`,
			phone: normalizedPhone,
			isActive,
			roleId,
		};

		if (password) {
			payload.password = password;
		}

		if (username.trim()) {
			payload.username = username.trim();
		}
		if (email.trim()) {
			payload.email = email.trim();
		}

		onSubmit(user.id, payload);
	};

	return (
		<Modal
			open={open}
			onCancel={handleClose}
			footer={null}
			title="Foydalanuvchini tahrirlash"
			width={560}
			className="crm-users-modal"
		>
			<div className="crm-users-modal-body">
				<div className="crm-inline-fields">
					<label className="crm-field" htmlFor="edit-user-first-name">
						<span>Ism</span>
						<Input
							id="edit-user-first-name"
							value={firstName}
							onChange={(e) => setFirstName(e.target.value)}
						/>
					</label>
					<label className="crm-field" htmlFor="edit-user-last-name">
						<span>Familiya</span>
						<Input
							id="edit-user-last-name"
							value={lastName}
							onChange={(e) => setLastName(e.target.value)}
						/>
					</label>
				</div>

				<div className="crm-inline-fields">
					<label className="crm-field" htmlFor="edit-user-username">
						<span>Username (login)</span>
						<Input
							id="edit-user-username"
							value={username}
							onChange={(e) => setUsername(e.target.value)}
						/>
					</label>

					<label className="crm-field" htmlFor="edit-user-email">
						<span>Email</span>
						<Input id="edit-user-email" value={email} onChange={(e) => setEmail(e.target.value)} />
					</label>
				</div>

				<div className="crm-inline-fields">
					<label className="crm-field" htmlFor="edit-user-phone">
						<span>Telefon raqam</span>
						<UzPhoneInput id="edit-user-phone" value={phone} onChange={setPhone} />
					</label>

					<label className="crm-field" htmlFor="edit-user-password">
						<span>Parol</span>
						<Input.Password
							id="edit-user-password"
							placeholder="Yangi parol"
							value={password}
							onChange={(e) => setPassword(e.target.value)}
						/>
					</label>
				</div>

				<div className="crm-inline-fields">
					<label className="crm-field" htmlFor="edit-user-role">
						<span>Rol</span>
						<Select
							allowClear
							id="edit-user-role"
							value={roleId}
							onChange={setRoleId}
							style={{ width: "100%" }}
							loading={rolesLoading}
							notFoundContent={rolesError ? "Ro'llarni yuklab bo'lmadi" : "Ro'llar topilmadi"}
							options={editableRoles.map((item) => ({
								label: ROLE_NAME_MAP[item.name] || item.name,
								value: item.id,
							}))}
						/>
					</label>

					<label className="crm-field" htmlFor="edit-user-status">
						<span>Holati</span>
						<Select
							id="edit-user-status"
							value={isActive}
							onChange={setIsActive}
							style={{ width: "100%" }}
							options={[
								{ label: "Faol", value: true },
								{ label: "Faol emas", value: false },
							]}
						/>
					</label>
				</div>

				<div className="crm-users-modal-footer">
					<Button disabled={isSubmitting} onClick={handleClose}>
						Bekor
					</Button>
					<Button
						type="primary"
						className="crm-primary-btn"
						loading={isSubmitting}
						onClick={handleSubmit}
					>
						Saqlash
					</Button>
				</div>
			</div>
		</Modal>
	);
}

export function UsersPage() {
	const { role } = useOutletContext<{ role: AppRole }>();
	const navigate = useNavigate();
	const location = useLocation();
	const fromDashboard = (location.state as any)?.fromDashboard === true;
	const [searchParams] = useSearchParams();
	const selectedOrgId = searchParams.get("orgId") || undefined;
	const currentUserId = useAuthStore((state) => state.user?.id);
	const canManage = canManageUsers(role);
	const [modalOpen, setModalOpen] = useState(false);
	const [editModalOpen, setEditModalOpen] = useState(false);
	const [editingUser, setEditingUser] = useState<AdminUser | null>(null);
	const queryClient = useQueryClient();

	const usersQuery = useQuery({
		queryKey: ["admin-users", selectedOrgId],
		queryFn: () => getAdminUsers({ orgId: selectedOrgId }),
	});
	const rolesQuery = useQuery({
		queryKey: ["admin-roles"],
		queryFn: getAdminRoles,
	});
	const organizationsQuery = useQuery({
		queryKey: ["admin-organizations", "users-create"],
		queryFn: () => organizationsApi.getOrganizations({ limit: 100 }),
		enabled: role === "super",
	});
	const adminUserDetailQuery = useQuery({
		queryKey: ["admin-user", editingUser?.id],
		queryFn: () => {
			if (!editingUser) {
				throw new Error("Admin user is not selected");
			}

			return getAdminUserById(editingUser.id);
		},
		enabled: editModalOpen && Boolean(editingUser?.id),
	});

	const createUserMutation = useMutation({
		mutationFn: submitUserCreate,
		onSuccess: async () => {
			message.success("Foydalanuvchi muvaffaqiyatli qo'shildi!");
			setModalOpen(false);
			await queryClient.invalidateQueries({ queryKey: ["admin-users"] });
		},
		onError: (error) => {
			message.error(parseApiError(error));
		},
	});

	const updateUserMutation = useMutation({
		mutationFn: ({ userId, payload }: { userId: string; payload: AdminUserUpdatePayload }) =>
			submitUserUpdate(userId, payload),
		onSuccess: async () => {
			message.success("Foydalanuvchi muvaffaqiyatli tahrirlandi!");
			setEditModalOpen(false);
			setEditingUser(null);
			await queryClient.invalidateQueries({ queryKey: ["admin-users"] });
		},
		onError: (error) => {
			message.error(parseApiError(error));
		},
	});

	const deleteUserMutation = useMutation({
		mutationFn: submitUserDelete,
		onSuccess: async () => {
			message.success("Foydalanuvchi muvaffaqiyatli o'chirildi!");
			await queryClient.invalidateQueries({ queryKey: ["admin-users"] });
		},
		onError: (error) => {
			message.error(parseApiError(error));
		},
	});

	const FALLBACK_ROLES: AdminRole[] = [
		{ id: "super_admin", name: "Super Administrator" },
		{ id: "org_admin", name: "Tashkilot Administratori" },
		{ id: "sales_manager", name: "Sotuv Menejeri" },
	];

	const users = (usersQuery.data?.items ?? []).filter((user: AdminUser) =>
		isManagedUserVisible(role, currentUserId, user)
	);
	const roles = rolesQuery.isError ? FALLBACK_ROLES : (rolesQuery.data ?? []);
	const rolesLoading = rolesQuery.isLoading && !rolesQuery.isError;
	const rolesError = rolesQuery.isError && !rolesQuery.data?.length;
	const organizations = organizationsQuery.data ?? [];
	const selectedOrganization = selectedOrgId
		? organizations.find((organization) => organization.id === selectedOrgId)
		: undefined;
	const organizationsLoading = organizationsQuery.isLoading;
	const organizationsError = organizationsQuery.isError;

	return (
		<div className="crm-users-page">
			<div className="crm-page-head">
				<div>
					<div style={{ display: "flex", alignItems: "center", gap: 12 }}>
						{fromDashboard && (
							<Button
								type="text"
								icon={<LeftOutlined />}
								onClick={() => navigate("/dashboard")}
								style={{ color: "var(--crm-text-secondary)" }}
							/>
						)}
						<h1>Foydalanuvchilar</h1>
					</div>
					<p>
						{fromDashboard ? (
							<span style={{ color: "var(--crm-text-secondary)" }}>Bosh sahifa / </span>
						) : null}
						Admin foydalanuvchilar va ularning rollari
					</p>
				</div>
				<Button
					className="crm-primary-btn"
					icon={<PlusOutlined />}
					onClick={() => {
						if (!canManage) {
							message.warning("Bu rol foydalanuvchilarni boshqara olmaydi.");
							return;
						}

						setModalOpen(true);
					}}
				>
					Qo'shish
				</Button>
			</div>

			<UsersNavigation activeTab="users" />

			{selectedOrgId ? (
				<div className="crm-users-scope-banner">
					<div>
						<strong>
							{organizationsLoading
								? "Tashkilot foydalanuvchilari yuklanmoqda"
								: `${selectedOrganization?.name ?? "Tanlangan tashkilot"} foydalanuvchilari`}
						</strong>
						<span>Faqat shu tashkilotga biriktirilgan foydalanuvchilar ko'rsatilmoqda.</span>
					</div>
					<Button onClick={() => navigate("/users")}>Barchasini ko'rsatish</Button>
				</div>
			) : null}

			<div className="crm-card crm-table-card crm-users-table-card">
				<div className="crm-table-wrap">
					<table className="crm-table crm-users-table">
						<thead>
							<tr>
								<th>F.I.O</th>
								<th>Username</th>
								<th>Email</th>
								<th>Rol</th>
								<th>Oxirgi kirish</th>
								<th>Holat</th>
								<th />
							</tr>
						</thead>
						<tbody>
							{usersQuery.isLoading ? (
								<tr>
									<td colSpan={7}>Yuklanmoqda...</td>
								</tr>
							) : usersQuery.isError ? (
								<tr>
									<td colSpan={7}>Foydalanuvchilarni yuklab bo'lmadi.</td>
								</tr>
							) : users.length === 0 ? (
								<tr>
									<td colSpan={7}>Foydalanuvchilar topilmadi.</td>
								</tr>
							) : (
								users.map((user: AdminUser) => (
									<tr key={user.id}>
										<td>
											<div className="crm-users-name-cell">
												<div className={`crm-mini-avatar ${getAvatarColor(user.id)}`}>
													{getInitials(user.fullName)}
												</div>
												<strong>{user.fullName}</strong>
											</div>
										</td>

										<td className="crm-users-muted-cell">{user.username}</td>
										<td className="crm-users-muted-cell">{user.email}</td>

										<td>
											<span className="crm-badge sky">
												{ROLE_NAME_MAP[getRoleLabel(user.role, roles)] ||
													getRoleLabel(user.role, roles)}
											</span>
										</td>

										<td className="crm-users-muted-cell">{formatLastLogin(user.lastLoginAt)}</td>

										<td>
											<span className={`crm-status-badge ${user.isActive ? "green" : "red"}`}>
												<span />
												{user.isActive ? "Faol" : "Faol emas"}
											</span>
										</td>

										<td>
											<div className="crm-users-actions">
												<Button
													icon={<EditOutlined />}
													size="small"
													className="crm-users-action-btn"
													aria-label={`${user.fullName} tahrirlash`}
													onClick={() => {
														if (!canManage) {
															message.warning("Bu rol foydalanuvchilarni boshqara olmaydi.");
															return;
														}
														setEditingUser(user);
														setEditModalOpen(true);
													}}
												/>
												<Popconfirm
													title="Foydalanuvchini o'chirish"
													description="Rostdan ham ushbu foydalanuvchini o'chirmoqchimisiz?"
													overlayClassName="crm-delete-popconfirm"
													onConfirm={() => {
														if (!canManage) {
															message.warning("Bu rol foydalanuvchilarni boshqara olmaydi.");
															return;
														}
														deleteUserMutation.mutate(user.id);
													}}
													okText="Ha"
													cancelText="Yo'q"
													okButtonProps={{ danger: true }}
												>
													<Button
														icon={<DeleteOutlined />}
														size="small"
														className="crm-users-action-btn crm-users-delete-btn"
														aria-label={`${user.fullName} o'chirish`}
													/>
												</Popconfirm>
											</div>
										</td>
									</tr>
								))
							)}
						</tbody>
					</table>
				</div>
				<div className="crm-table-foot">
					<span>Jami {users.length} ta foydalanuvchi</span>
				</div>
			</div>

			<AddUserModal
				canManage={canManage}
				currentRole={role}
				isSubmitting={createUserMutation.isPending}
				open={modalOpen}
				roles={roles}
				rolesLoading={rolesLoading}
				rolesError={rolesError}
				organizations={organizations}
				organizationsLoading={organizationsLoading}
				organizationsError={organizationsError}
				onClose={() => setModalOpen(false)}
				onSubmit={(payload) => createUserMutation.mutate({ ...payload, isActive: true })}
			/>

			<EditUserModal
				canManage={canManage}
				currentRole={role}
				isSubmitting={updateUserMutation.isPending || adminUserDetailQuery.isFetching}
				open={editModalOpen}
				roles={roles}
				rolesLoading={rolesLoading}
				rolesError={rolesError}
				user={adminUserDetailQuery.data ?? editingUser}
				onClose={() => {
					setEditModalOpen(false);
					setEditingUser(null);
				}}
				onSubmit={(userId, payload) => updateUserMutation.mutate({ userId, payload })}
			/>
		</div>
	);
}

export default UsersPage;
